// Supabase session storage in cookies instead of localStorage.
//
// /RefreshToken runs in an iframe on the company apps. When the app is on another site (hr-system on devsoft.vn
// framing the songtek.net login page), the browser partitions the iframe's localStorage, so a session created by
// signing in on a top-level tab is invisible to it. SameSite=None cookies are not partitioned, which is also how the
// SuperTokens version shared its session with the iframe.
//
// A session is a few KB (access token + user), more than one cookie holds, so long values are split into
// <key>.0, <key>.1, ...

export interface CookieJar {
    get (): string;
    set (cookie: string): void;
}

export const CHUNK_SIZE = 3000;
// Browsers cap cookie lifetime at 400 days.
const ATTRIBUTES = 'Path=/; Max-Age=34560000; SameSite=None; Secure';

export const documentCookies: CookieJar = {
    get: () => document.cookie,
    set: cookie => { document.cookie = cookie; }
};

function readAll (jar: CookieJar): Map<string, string> {
    const cookies = new Map<string, string>();
    for (const pair of jar.get().split(';')) {
        const index = pair.indexOf('=');
        if (index > 0) {
            cookies.set(pair.slice(0, index).trim(), pair.slice(index + 1).trim());
        }
    }
    return cookies;
}

function namesOf (key: string, cookies: Map<string, string>): string[] {
    const chunk = new RegExp(`^${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\.\\d+$`);
    return [...cookies.keys()].filter(name => name === key || chunk.test(name));
}

export function cookieStorage (jar: CookieJar = documentCookies) {
    function removeItem (key: string) {
        for (const name of namesOf(key, readAll(jar))) {
            jar.set(`${name}=; Path=/; Max-Age=0; SameSite=None; Secure`);
        }
    }

    return {
        getItem (key: string): string | null {
            const cookies = readAll(jar);
            if (cookies.has(key)) {
                return decodeURIComponent(cookies.get(key)!);
            }
            const chunks: string[] = [];
            for (let i = 0; cookies.has(`${key}.${i}`); i++) {
                chunks.push(cookies.get(`${key}.${i}`)!);
            }
            return chunks.length ? decodeURIComponent(chunks.join('')) : null;
        },
        setItem (key: string, value: string) {
            removeItem(key);
            const encoded = encodeURIComponent(value);
            if (encoded.length <= CHUNK_SIZE) {
                jar.set(`${key}=${encoded}; ${ATTRIBUTES}`);
                return;
            }
            // Chunks are joined before decoding, so cutting through a %XX escape is fine.
            for (let i = 0; i * CHUNK_SIZE < encoded.length; i++) {
                jar.set(`${key}.${i}=${encoded.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)}; ${ATTRIBUTES}`);
            }
        },
        removeItem
    };
}
